import { useState, useEffect } from 'react';
import { integrationsAPI } from '@/lib/api';
import TuerAnleitungKarte from '@/components/TuerAnleitungKarte';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import {
  MessageSquare, Phone, Mail, Shield, Save, CheckCircle2,
  AlertTriangle, Eye, Plus, X, Loader2, ExternalLink, Plug, Lock, CreditCard,
  Dumbbell, Copy, RefreshCw, Send, Users, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

export default function IntegrationsPage({ kanal = 'sms' }) {
  const [twilioConfig, setTwilioConfig] = useState({
    account_sid: '',
    phone_number: '',
    auth_token_masked: '',
    connection_verified: false
  });
  const [emailConfig, setEmailConfig] = useState(null);
  const [brevoKey, setBrevoKey] = useState('');
  const [testingBrevo, setTestingBrevo] = useState(false);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [nukiConfig, setNukiConfig] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [oneClickGymConfig, setOneClickGymConfig] = useState(null);
  const [regeneratingSecret, setRegeneratingSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingTwilio, setTestingTwilio] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingNuki, setTestingNuki] = useState(false);
  const [searchingNuki, setSearchingNuki] = useState(false);
  const [nukiLocks, setNukiLocks] = useState(null);
  const [testingStripe, setTestingStripe] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [showEmailKey, setShowEmailKey] = useState(false);
  const [showNukiToken, setShowNukiToken] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showStripeWebhookSecret, setShowStripeWebhookSecret] = useState(false);
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [emailApiKey, setEmailApiKey] = useState('');
  const [nukiApiToken, setNukiApiToken] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    const fetch = async () => {
      try {
        const [tRes, eRes, nRes, sRes, oRes] = await Promise.all([integrationsAPI.get(), integrationsAPI.getEmailConfig(), integrationsAPI.getNukiConfig(), integrationsAPI.getStripeConfig(), integrationsAPI.getOneClickGymConfig()]);
        setTwilioConfig(tRes.data || {
          account_sid: '',
          phone_number: '',
          auth_token_masked: '',
          connection_verified: false
        });
        setEmailConfig(eRes.data);
        setNukiConfig(nRes.data);
        setStripeConfig(sRes.data);
        setOneClickGymConfig(oRes.data);
      } catch (e) {  } finally { setLoading(false); }
    };
    fetch();
  }, [isAdmin]);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const tp = { ...twilioConfig };
      if (twilioAuthToken) tp.auth_token = twilioAuthToken;
      delete tp.auth_token_masked; delete tp.connection_verified; delete tp.verified_at; delete tp.updated_at;
      await integrationsAPI.update(tp);

      const ep = { ...emailConfig };
      if (emailApiKey) ep.api_key = emailApiKey;
      if (brevoKey) ep.brevo_api_key = brevoKey;
      delete ep.api_key_masked; delete ep.brevo_api_key_masked; delete ep.webhook_url; delete ep.connection_verified; delete ep.verified_at; delete ep.updated_at;
      await integrationsAPI.updateEmailConfig(ep);

      const np = { ...nukiConfig };
      if (nukiApiToken) np.api_token = nukiApiToken;
      delete np.api_token_masked; delete np.connection_verified; delete np.verified_at; delete np.updated_at;
      await integrationsAPI.updateNukiConfig(np);

      const sp = { ...stripeConfig };
      if (stripeSecretKey) sp.secret_key = stripeSecretKey;
      if (stripeWebhookSecret) sp.webhook_secret = stripeWebhookSecret;
      delete sp.secret_key_masked; delete sp.webhook_secret_masked; delete sp.connection_verified; delete sp.verified_at; delete sp.updated_at;
      await integrationsAPI.updateStripeConfig(sp);

      await integrationsAPI.updateOneClickGymConfig({ enabled: oneClickGymConfig.enabled });

      toast.success('Alle Einstellungen gespeichert');
      const [tRes, eRes, nRes, sRes, oRes] = await Promise.all([integrationsAPI.get(), integrationsAPI.getEmailConfig(), integrationsAPI.getNukiConfig(), integrationsAPI.getStripeConfig(), integrationsAPI.getOneClickGymConfig()]);
      setTwilioConfig(tRes.data); setEmailConfig(eRes.data); setNukiConfig(nRes.data); setStripeConfig(sRes.data); setOneClickGymConfig(oRes.data);
      setTwilioAuthToken(''); setEmailApiKey(''); setBrevoKey(''); setNukiApiToken(''); setStripeSecretKey(''); setStripeWebhookSecret('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleRegenerateOneClickGymSecret = async () => {
    if (!window.confirm('Neues Secret erzeugen? Die bisherige Webhook-URL wird ungültig — in OneClickGym muss die neue URL eingetragen werden.')) return;
    setRegeneratingSecret(true);
    try {
      const res = await integrationsAPI.regenerateOneClickGymSecret();
      setOneClickGymConfig(p => ({ ...p, webhook_path: res.data.webhook_path }));
      toast.success('Neues Secret erzeugt');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setRegeneratingSecret(false); }
  };

  const copyOneClickGymUrl = () => {
    navigator.clipboard.writeText(`${window.location.origin}${oneClickGymConfig.webhook_path}`);
    toast.success('Webhook-URL kopiert');
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (!isAdmin) {
    return (
      <div className="space-y-5 fade-in" data-testid="integrations-page">
        <h1 className="text-2xl font-bold tracking-tight">Integrationen</h1>
        <Card className="border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-8 text-center text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Only administrators can manage integrations.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!twilioConfig || !emailConfig || !nukiConfig || !stripeConfig || !oneClickGymConfig) return null;

  const VARS = '{lead_name} {agent_name} {company} {date} {property_address} {property_title} {property_price}';

  return (
    <div className="space-y-6 fade-in max-w-4xl" data-testid="integrations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Integrationen</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Nachrichtenkanäle konfigurieren</p>
        </div>
        <Button onClick={handleSaveAll} disabled={saving} data-testid="save-integrations-btn" className="gap-2">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      <Tabs defaultValue={kanal} key={kanal} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="sms" data-testid="tab-sms" className="gap-1.5 text-xs"><Phone className="w-3.5 h-3.5" />SMS</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp" className="gap-1.5 text-xs"><MessageSquare className="w-3.5 h-3.5" />WhatsApp</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email" className="gap-1.5 text-xs"><Mail className="w-3.5 h-3.5" />Email</TabsTrigger>
          <TabsTrigger value="nuki" data-testid="tab-nuki" className="gap-1.5 text-xs"><Lock className="w-3.5 h-3.5" />Nuki</TabsTrigger>
          <TabsTrigger value="stripe" data-testid="tab-stripe" className="gap-1.5 text-xs"><CreditCard className="w-3.5 h-3.5" />Stripe</TabsTrigger>
          <TabsTrigger value="oneclickgym" data-testid="tab-oneclickgym" className="gap-1.5 text-xs"><Dumbbell className="w-3.5 h-3.5" />OneClickGym</TabsTrigger>
        </TabsList>

        {/* ─── SMS ─── */}
        <TabsContent value="sms" className="mt-4 space-y-4">
          <ProviderCard
            icon={Phone} iconColor="text-blue-500" iconBg="bg-blue-100 dark:bg-blue-900/30"
            title="SMS via Twilio" verified={twilioConfig.connection_verified}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Account SID</Label>
                <Input value={twilioConfig.account_sid || ''} onChange={(e) => setTwilioConfig(p => ({...p, account_sid: e.target.value}))} placeholder="ACxxxxxxxx" className="h-9 mono text-xs" data-testid="twilio-sid-input" />
              </div>
              <div>
                <Label className="text-xs">Auth Token</Label>
                <div className="relative">
                  <Input type={showTwilioToken ? 'text' : 'password'} value={twilioAuthToken} onChange={(e) => setTwilioAuthToken(e.target.value)} placeholder={twilioConfig.auth_token_masked || 'Enter token'} className="h-9 mono text-xs pr-9" data-testid="twilio-token-input" />
                  <button onClick={() => setShowTwilioToken(!showTwilioToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={async () => { 
              setTestingTwilio(true); 
              try { 
                // Create test payload with current form values (all fields)
                const testPayload = {
                  account_sid: twilioConfig.account_sid,
                  auth_token: twilioAuthToken || twilioConfig.auth_token_masked, // Use current input if entered
                  phone_number: twilioConfig.phone_number
                };
                
                // Only send if we have values
                if (!testPayload.account_sid) {
                  toast.error('Bitte zuerst die Account SID eingeben');
                  setTestingTwilio(false);
                  return;
                }
                
                const r = await integrationsAPI.testConnection(testPayload); 
                toast.success(r.data.message || `Connected: ${r.data.account_name}`); 
                setTwilioConfig(p => ({...p, connection_verified: true})); 
              } catch(e) { 
                toast.error(e.response?.data?.detail || 'Connection failed'); 
              } finally { 
                setTestingTwilio(false); 
              }
            }} disabled={testingTwilio || !twilioConfig.account_sid || (!twilioAuthToken && !twilioConfig.auth_token_masked)} className="gap-1.5 h-8 text-xs" data-testid="test-twilio-btn">
              {testingTwilio ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />} Test Connection
            </Button>
          </ProviderCard>

          <ChannelConfig
            icon={Phone} iconColor="text-blue-500" title="SMS Messaging"
            enabled={twilioConfig.sms_enabled} onToggle={(v) => setTwilioConfig(p => ({...p, sms_enabled: v}))}
          >
            <div>
              <Label className="text-xs">From Number (E.164)</Label>
              <Input value={twilioConfig.sms_from_number || ''} onChange={(e) => setTwilioConfig(p => ({...p, sms_from_number: e.target.value}))} placeholder="+14155551234" className="mono text-xs max-w-xs h-9" data-testid="sms-from-input" />
            </div>
            <TemplateList
              templates={twilioConfig.sms_templates || []}
              onAdd={() => setTwilioConfig(p => ({...p, sms_templates: [...(p.sms_templates||[]), {id:`t_${Date.now()}`, name:'New Template', body:'Hi {lead_name}, '}]}))}
              onUpdate={(i, f, v) => { const t = [...twilioConfig.sms_templates]; t[i] = {...t[i], [f]: v}; setTwilioConfig(p => ({...p, sms_templates: t})); }}
              onRemove={(i) => setTwilioConfig(p => ({...p, sms_templates: p.sms_templates.filter((_,j) => j!==i)}))}
              varsHint={VARS} hasSubject={false}
            />
          </ChannelConfig>
        </TabsContent>

        {/* ─── WhatsApp ─── */}
        <TabsContent value="whatsapp" className="mt-4 space-y-4">
          <ProviderCard
            icon={MessageSquare} iconColor="text-green-500" iconBg="bg-green-100 dark:bg-green-900/30"
            title="WhatsApp via Twilio" verified={twilioConfig.connection_verified}
          >
            {twilioConfig.account_sid ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                Using Twilio credentials from SMS tab (Account SID: {twilioConfig.account_sid?.slice(0,8)}...)
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Zuerst die Twilio-Zugangsdaten im SMS-Tab konfigurieren — WhatsApp nutzt dasselbe Konto.</p>
            )}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-900/40 text-xs text-green-700 dark:text-green-400">
              Requires Twilio WhatsApp Sandbox or approved Business number. <a href="https://www.twilio.com/docs/whatsapp/sandbox" target="_blank" rel="noopener noreferrer" className="underline font-medium">Learn more <ExternalLink className="w-3 h-3 inline" /></a>
            </div>
          </ProviderCard>

          <ChannelConfig
            icon={MessageSquare} iconColor="text-green-500" title="WhatsApp Messaging"
            enabled={twilioConfig.whatsapp_enabled} onToggle={(v) => setTwilioConfig(p => ({...p, whatsapp_enabled: v}))}
          >
            <div>
              <Label className="text-xs">From Number (E.164)</Label>
              <Input value={twilioConfig.whatsapp_from_number || ''} onChange={(e) => setTwilioConfig(p => ({...p, whatsapp_from_number: e.target.value}))} placeholder="+14155238886" className="mono text-xs max-w-xs h-9" data-testid="whatsapp-from-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Twilio Sandbox default: +14155238886</p>
            </div>
            <TemplateList
              templates={twilioConfig.whatsapp_templates || []}
              onAdd={() => setTwilioConfig(p => ({...p, whatsapp_templates: [...(p.whatsapp_templates||[]), {id:`t_${Date.now()}`, name:'New Template', body:'Hi {lead_name}, '}]}))}
              onUpdate={(i, f, v) => { const t = [...twilioConfig.whatsapp_templates]; t[i] = {...t[i], [f]: v}; setTwilioConfig(p => ({...p, whatsapp_templates: t})); }}
              onRemove={(i) => setTwilioConfig(p => ({...p, whatsapp_templates: p.whatsapp_templates.filter((_,j) => j!==i)}))}
              varsHint={VARS} hasSubject={false}
            />
          </ChannelConfig>
        </TabsContent>

        {/* ─── Email ─── */}
        <TabsContent value="email" className="mt-4 space-y-4">
          <ProviderCard
            icon={Mail} iconColor="text-indigo-500" iconBg="bg-indigo-100 dark:bg-indigo-900/30"
            title="Email" verified={emailConfig.connection_verified}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Provider</Label>
                <select
                  value={emailConfig.provider || 'sendgrid'}
                  onChange={(e) => setEmailConfig(p => ({...p, provider: e.target.value}))}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  data-testid="email-provider-select"
                >
                  <option value="sendgrid">SendGrid</option>
                  <option value="resend">Resend</option>
                  <option value="smtp">SMTP (z. B. IONOS)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">{emailConfig.provider === 'smtp' ? 'SMTP-Passwort' : 'API Key'}</Label>
                <div className="relative">
                  <Input type={showEmailKey ? 'text' : 'password'} value={emailApiKey} onChange={(e) => setEmailApiKey(e.target.value)} placeholder={emailConfig.api_key_masked || (emailConfig.provider === 'smtp' ? 'Postfach-Passwort' : 'Enter API key')} className="h-9 mono text-xs pr-9" data-testid="email-key-input" />
                  <button onClick={() => setShowEmailKey(!showEmailKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
            {emailConfig.provider === 'smtp' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="smtp-fields">
                <div>
                  <Label className="text-xs">SMTP-Server</Label>
                  <Input value={emailConfig.smtp_host ?? 'smtp.ionos.de'} onChange={(e) => setEmailConfig(p => ({...p, smtp_host: e.target.value}))} placeholder="smtp.ionos.de" className="h-9 text-xs" data-testid="smtp-host" />
                </div>
                <div>
                  <Label className="text-xs">Port</Label>
                  <Input type="number" value={emailConfig.smtp_port ?? 587} onChange={(e) => setEmailConfig(p => ({...p, smtp_port: e.target.value}))} placeholder="587" className="h-9 text-xs" data-testid="smtp-port" />
                </div>
                <div>
                  <Label className="text-xs">Benutzer (E-Mail-Adresse)</Label>
                  <Input value={emailConfig.smtp_user || ''} onChange={(e) => setEmailConfig(p => ({...p, smtp_user: e.target.value}))} placeholder="info@p1-pforzheim.de" className="h-9 text-xs" data-testid="smtp-user" />
                </div>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={async () => { setTestingEmail(true); try { const ep = { ...emailConfig }; if (emailApiKey) ep.api_key = emailApiKey; delete ep.api_key_masked; delete ep.connection_verified; delete ep.verified_at; delete ep.updated_at; await integrationsAPI.updateEmailConfig(ep); const r = await integrationsAPI.testEmailConnection(); toast.success(`Connected via ${r.data.provider}`); setEmailConfig(p => ({...p, connection_verified: true})); const eRes = await integrationsAPI.getEmailConfig(); setEmailConfig(eRes.data); setEmailApiKey(''); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setTestingEmail(false); }}} disabled={testingEmail || (!emailApiKey && !emailConfig.api_key_masked)} className="gap-1.5 h-8 text-xs" data-testid="test-email-btn">
              {testingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />} Test Connection
            </Button>
            {/* Versandweg (23.09.2026): Brevo liefert Zustellung/Bounce per Webhook zurueck; IONOS bleibt fuer den Posteingang */}
            <div className="rounded-lg border border-border/60 p-3 space-y-3" data-testid="versandweg">
              <div className="flex items-center gap-4 flex-wrap text-xs">
                <span className="font-semibold uppercase tracking-wider text-muted-foreground">Versandweg</span>
                {[['bestand', 'Postfach oben (IONOS/SMTP)'], ['brevo', 'Brevo (mit Zustell-Rückmeldung)']].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name="adapter" className="accent-marke" checked={(emailConfig.adapter || 'bestand') === k} onChange={() => setEmailConfig(p => ({ ...p, adapter: k }))} data-testid={`adapter-${k}`} /> {l}</label>
                ))}
              </div>
              {(emailConfig.adapter || 'bestand') === 'brevo' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Brevo API-Schlüssel</Label>
                      <Input type="password" value={brevoKey} onChange={(e) => setBrevoKey(e.target.value)} placeholder={emailConfig.brevo_api_key_masked || 'xkeysib-…'} className="h-9 mono text-xs" data-testid="brevo-key" />
                    </div>
                    <div>
                      <Label className="text-xs">Webhook-Token (in Brevo als Header X-Webhook-Token)</Label>
                      <div className="flex gap-1.5">
                        <Input value={emailConfig.webhook_token || ''} onChange={(e) => setEmailConfig(p => ({ ...p, webhook_token: e.target.value }))} className="h-9 mono text-xs" data-testid="webhook-token" />
                        <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => setEmailConfig(p => ({ ...p, webhook_token: Array.from(crypto.getRandomValues(new Uint8Array(24))).map(b => b.toString(16).padStart(2, '0')).join('') }))}>neu</Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Webhook-Adresse für Brevo (Transactional → Webhooks, Ereignisse: delivered, hard bounce, soft bounce, spam, unsubscribed, opened): <code className="font-mono select-all">{emailConfig.webhook_url}</code></p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" disabled={testingBrevo || (!brevoKey && !emailConfig.brevo_api_key_masked)} data-testid="test-brevo"
                      onClick={async () => { setTestingBrevo(true); try { const ep = { ...emailConfig }; if (brevoKey) ep.brevo_api_key = brevoKey; if (emailApiKey) ep.api_key = emailApiKey; delete ep.api_key_masked; delete ep.brevo_api_key_masked; delete ep.webhook_url; delete ep.connection_verified; delete ep.verified_at; delete ep.updated_at; await integrationsAPI.updateEmailConfig(ep); const r = await integrationsAPI.testBrevo(); toast.success(`Brevo verbunden: ${r.data.email || ''}${r.data.kontingent != null ? ` · ${r.data.kontingent} Mails Kontingent` : ''}`); const eRes = await integrationsAPI.getEmailConfig(); setEmailConfig(eRes.data); setBrevoKey(''); } catch (e) { toast.error(e.response?.data?.detail || 'Brevo-Test fehlgeschlagen'); } finally { setTestingBrevo(false); } }}>
                      {testingBrevo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />} Brevo testen
                    </Button>
                    {emailConfig.brevo_verified_at && <span className="text-xs text-emerald-600">verbunden als {emailConfig.brevo_konto} · {new Date(emailConfig.brevo_verified_at).toLocaleString('de-DE')}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" disabled={webhookBusy || !emailConfig.brevo_verified_at || !emailConfig.webhook_token} data-testid="brevo-webhook"
                      onClick={async () => { setWebhookBusy(true); try { const r = await integrationsAPI.brevoWebhook(); toast.success(r.data.neu ? 'Webhook bei Brevo angelegt' : 'Webhook bei Brevo aktualisiert'); const eRes = await integrationsAPI.getEmailConfig(); setEmailConfig(eRes.data); } catch (e) { toast.error(e.response?.data?.detail || 'Webhook konnte nicht angelegt werden'); } finally { setWebhookBusy(false); } }}>
                      {webhookBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />} Webhook bei Brevo anlegen
                    </Button>
                    {emailConfig.brevo_webhook_am && <span className="text-xs text-emerald-600">Webhook Nr. {emailConfig.brevo_webhook_id} · {new Date(emailConfig.brevo_webhook_am).toLocaleString('de-DE')}</span>}
                    {!emailConfig.webhook_token && <span className="text-xs text-muted-foreground">erst Token „neu" und speichern</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">Absender unten muss eine bei Brevo verifizierte Domain sein (p1-pforzheim.de). Der Posteingang läuft weiter über das IONOS-Postfach oben.</p>
                </div>
              )}
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 border border-indigo-200 dark:border-indigo-900/40 text-xs text-indigo-700 dark:text-indigo-400">
              {emailConfig.provider === 'sendgrid' ? (
                <>API-Schlüssel abrufen unter <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">SendGrid-Einstellungen <ExternalLink className="w-3 h-3 inline" /></a></>
              ) : emailConfig.provider === 'smtp' ? (
                <>IONOS: Server <b>smtp.ionos.de</b>, Port <b>587</b> (oder 465), Benutzer = deine vollständige E-Mail-Adresse, Passwort = dein Postfach-Passwort. „From Email" unten muss dieselbe Adresse sein. Hinweis: IONOS-Postfächer haben Tageslimits — für sehr große Verteiler eher ungeeignet.</>
              ) : (
                <>API-Schlüssel abrufen unter <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Resend-Dashboard <ExternalLink className="w-3 h-3 inline" /></a></>
              )}
            </div>
          </ProviderCard>

          <ChannelConfig
            icon={Mail} iconColor="text-indigo-500" title="Email"
            enabled={emailConfig.email_enabled} onToggle={(v) => setEmailConfig(p => ({...p, email_enabled: v}))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From Email</Label>
                <Input value={emailConfig.from_email || ''} onChange={(e) => setEmailConfig(p => ({...p, from_email: e.target.value}))} placeholder="name@ihre-domain.de" className="text-xs h-9" data-testid="email-from-input" />
              </div>
              <div>
                <Label className="text-xs">From Name</Label>
                <Input value={emailConfig.from_name || ''} onChange={(e) => setEmailConfig(p => ({...p, from_name: e.target.value}))} placeholder="PropNex CRM" className="text-xs h-9" data-testid="email-from-name" />
              </div>
            </div>
            <TemplateList
              templates={emailConfig.templates || []}
              onAdd={() => setEmailConfig(p => ({...p, templates: [...(p.templates||[]), {id:`t_${Date.now()}`, name:'New Template', subject:'Subject - {company}', body:'Hi {lead_name},\n\n'}]}))}
              onUpdate={(i, f, v) => { const t = [...emailConfig.templates]; t[i] = {...t[i], [f]: v}; setEmailConfig(p => ({...p, templates: t})); }}
              onRemove={(i) => setEmailConfig(p => ({...p, templates: p.templates.filter((_,j) => j!==i)}))}
              varsHint={VARS} hasSubject={true}
            />
          </ChannelConfig>

          {/* Massen-E-Mail (Kommandozentrale) */}
          <BulkEmailCard />
        </TabsContent>

        {/* ─── Nuki Smart Lock ─── */}
        <TabsContent value="nuki" className="mt-4 space-y-4">
          <ProviderCard
            icon={Lock} iconColor="text-red-500" iconBg="bg-red-100 dark:bg-red-900/30"
            title="Türöffnung via Nuki" verified={nukiConfig.connection_verified}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Smartlock-ID</Label>
                <Input value={nukiConfig.smartlock_id || ''} onChange={(e) => setNukiConfig(p => ({...p, smartlock_id: e.target.value}))} placeholder="12345678901" className="h-9 mono text-xs" data-testid="nuki-smartlock-input" />
              </div>
              <div>
                <Label className="text-xs">API-Token</Label>
                <div className="relative">
                  <Input type={showNukiToken ? 'text' : 'password'} value={nukiApiToken} onChange={(e) => setNukiApiToken(e.target.value)} placeholder={nukiConfig.api_token_masked || 'Token eingeben'} className="h-9 mono text-xs pr-9" data-testid="nuki-token-input" />
                  <button onClick={() => setShowNukiToken(!showNukiToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={async () => {
                setSearchingNuki(true); setNukiLocks(null);
                try {
                  const np = { ...nukiConfig };
                  if (nukiApiToken) np.api_token = nukiApiToken;
                  delete np.api_token_masked; delete np.connection_verified; delete np.verified_at; delete np.updated_at;
                  await integrationsAPI.updateNukiConfig(np);
                  const r = await integrationsAPI.listNukiSmartlocks();
                  const locks = r.data.smartlocks || [];
                  if (locks.length === 0) {
                    toast.error('Keine Schlösser gefunden — ist Nuki Web für das Schloss aktiviert?');
                  } else if (locks.length === 1) {
                    setNukiConfig(p => ({ ...p, smartlock_id: locks[0].smartlock_id }));
                    toast.success(`Gefunden: ${locks[0].name} — ID eingetragen`);
                  } else {
                    setNukiLocks(locks);
                  }
                } catch (e) { toast.error(e.response?.data?.detail || 'Suche fehlgeschlagen'); }
                finally { setSearchingNuki(false); }
              }} disabled={searchingNuki || (!nukiApiToken && !nukiConfig.api_token_masked)} className="gap-1.5 h-8 text-xs" data-testid="find-nuki-btn">
                {searchingNuki ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Schloss automatisch finden
              </Button>
              <Button variant="outline" size="sm" onClick={async () => {
                setTestingNuki(true);
                try {
                  const np = { ...nukiConfig };
                  if (nukiApiToken) np.api_token = nukiApiToken;
                  delete np.api_token_masked; delete np.connection_verified; delete np.verified_at; delete np.updated_at;
                  await integrationsAPI.updateNukiConfig(np);
                  const r = await integrationsAPI.testNukiConnection();
                  toast.success(`Verbunden: ${r.data.smartlock_name || 'Schloss gefunden'}`);
                  const nRes = await integrationsAPI.getNukiConfig();
                  setNukiConfig(nRes.data); setNukiApiToken('');
                } catch (e) { toast.error(e.response?.data?.detail || 'Verbindung fehlgeschlagen'); }
                finally { setTestingNuki(false); }
              }} disabled={testingNuki || !nukiConfig.smartlock_id || (!nukiApiToken && !nukiConfig.api_token_masked)} className="gap-1.5 h-8 text-xs" data-testid="test-nuki-btn">
                {testingNuki ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />} Test Connection
              </Button>
            </div>
            {nukiLocks && nukiLocks.length > 1 && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">Mehrere Schlösser gefunden — welches ist die Studiotür?</p>
                <div className="flex flex-wrap gap-2">
                  {nukiLocks.map(l => (
                    <Button key={l.smartlock_id} variant="outline" size="sm" className="h-8 text-xs"
                      onClick={() => { setNukiConfig(p => ({ ...p, smartlock_id: l.smartlock_id })); setNukiLocks(null); toast.success(`${l.name} — ID eingetragen`); }}>
                      🔒 {l.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-900/40 text-xs text-red-700 dark:text-red-400">
              API-Token aus dem <a href="https://web.nuki.io" target="_blank" rel="noopener noreferrer" className="underline font-medium">Nuki Web Portal <ExternalLink className="w-3 h-3 inline" /></a> erstellen. Öffnet die Tür nur bei aktiver Mitgliedschaft und kürzlichem Check-in (unten konfigurierbar) und erfordert eine verifizierte Mitglieds-Sitzung in der App — die Tür lässt sich nicht allein mit dem App-Key öffnen.
            </div>
          </ProviderCard>

          <ChannelConfig
            icon={Lock} iconColor="text-red-500" title="Türöffnung"
            enabled={nukiConfig.enabled} onToggle={(v) => setNukiConfig(p => ({...p, enabled: v}))}
          >
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Aktive Mitgliedschaft erforderlich</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Öffnet nur, wenn das Mitglied eine aktive Mitgliedschaft hat</p>
              </div>
              <Switch checked={nukiConfig.require_active_membership !== false} onCheckedChange={(v) => setNukiConfig(p => ({...p, require_active_membership: v}))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Mitarbeiter ohne Check-in</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Trainer und Admins öffnen jederzeit — sie schließen auf und können vorher nicht einchecken. Jede solche Öffnung wird im Türprotokoll als Mitarbeiter-Zugang vermerkt.</p>
              </div>
              <Switch checked={nukiConfig.staff_bypass_presence !== false} onCheckedChange={(v) => setNukiConfig(p => ({...p, staff_bypass_presence: v}))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Nur mit Gym-Zugang</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Öffnet nur für Performance Plus, Gym-Pass oder gebuchtes Gym &amp; Sauna — alte Unlimited- und Kurstarife nicht</p>
              </div>
              <Switch checked={nukiConfig.nur_mit_gym_zugang !== false} onCheckedChange={(v) => setNukiConfig(p => ({...p, nur_mit_gym_zugang: v}))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Anwesenheitsfenster (Minuten)</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Tür öffnet nur innerhalb dieser Zeit nach dem Scan des Codes „Mitglieder-Check-in“ am Willkommensschild — andere QR-Codes zählen für die Tür nicht. Erneutes Scannen schaltet sie wieder für diese Zeit frei (ohne zweiten Check-in); die App zeigt die Restzeit an.</p>
              </div>
              <Input
                type="number" min="1" max="120"
                value={nukiConfig.presence_window_minutes ?? 10}
                onChange={(e) => setNukiConfig(p => ({...p, presence_window_minutes: e.target.value}))}
                className="h-9 w-20 text-xs text-center flex-shrink-0"
                data-testid="nuki-presence-window-input"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-xs">Tür-Zeiten für Mitglieder</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Mitglieder öffnen die Tür per App nur in dieser Zeit (Gym &amp; Sauna laut Vereinbarung 6–22 Uhr). Trainer und Leitung öffnen jederzeit. Ausgeschaltet gilt keine Uhrzeit.</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Input
                  type="time" value={nukiConfig.zeit_von || '06:00'} disabled={nukiConfig.zeitfenster_aktiv === false}
                  onChange={(e) => setNukiConfig(p => ({...p, zeit_von: e.target.value}))}
                  className="h-9 w-24 text-xs text-center" aria-label="Tür öffnet ab" data-testid="nuki-zeit-von"
                />
                <span className="text-xs text-muted-foreground">bis</span>
                <Input
                  type="time" value={nukiConfig.zeit_bis || '22:00'} disabled={nukiConfig.zeitfenster_aktiv === false}
                  onChange={(e) => setNukiConfig(p => ({...p, zeit_bis: e.target.value}))}
                  className="h-9 w-24 text-xs text-center" aria-label="Tür öffnet bis" data-testid="nuki-zeit-bis"
                />
                <Switch checked={nukiConfig.zeitfenster_aktiv !== false} onCheckedChange={(v) => setNukiConfig(p => ({...p, zeitfenster_aktiv: v}))} data-testid="nuki-zeitfenster-switch" />
              </div>
            </div>
          </ChannelConfig>
          <TuerAnleitungKarte />
        </TabsContent>

        {/* ─── Stripe ─── */}
        <TabsContent value="stripe" className="mt-4 space-y-4">
          <ProviderCard
            icon={CreditCard} iconColor="text-violet-500" iconBg="bg-violet-100 dark:bg-violet-900/30"
            title="Zahlungen via Stripe" verified={stripeConfig.connection_verified}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Secret Key</Label>
                <div className="relative">
                  <Input type={showStripeKey ? 'text' : 'password'} value={stripeSecretKey} onChange={(e) => setStripeSecretKey(e.target.value)} placeholder={stripeConfig.secret_key_masked || 'sk_live_... oder sk_test_...'} className="h-9 mono text-xs pr-9" data-testid="stripe-key-input" />
                  <button onClick={() => setShowStripeKey(!showStripeKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Webhook Signing Secret</Label>
                <div className="relative">
                  <Input type={showStripeWebhookSecret ? 'text' : 'password'} value={stripeWebhookSecret} onChange={(e) => setStripeWebhookSecret(e.target.value)} placeholder={stripeConfig.webhook_secret_masked || 'whsec_...'} className="h-9 mono text-xs pr-9" data-testid="stripe-webhook-secret-input" />
                  <button onClick={() => setShowStripeWebhookSecret(!showStripeWebhookSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={async () => {
              setTestingStripe(true);
              try {
                const sp = { ...stripeConfig };
                if (stripeSecretKey) sp.secret_key = stripeSecretKey;
                if (stripeWebhookSecret) sp.webhook_secret = stripeWebhookSecret;
                delete sp.secret_key_masked; delete sp.webhook_secret_masked; delete sp.connection_verified; delete sp.verified_at; delete sp.updated_at;
                await integrationsAPI.updateStripeConfig(sp);
                const r = await integrationsAPI.testStripeConnection();
                toast.success(`Verbunden: Account ${r.data.account_id}`);
                const sRes = await integrationsAPI.getStripeConfig();
                setStripeConfig(sRes.data); setStripeSecretKey(''); setStripeWebhookSecret('');
              } catch (e) { toast.error(e.response?.data?.detail || 'Verbindung fehlgeschlagen'); }
              finally { setTestingStripe(false); }
            }} disabled={testingStripe || (!stripeSecretKey && !stripeConfig.secret_key_masked)} className="gap-1.5 h-8 text-xs" data-testid="test-stripe-btn">
              {testingStripe ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />} Test Connection
            </Button>
            <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3 border border-violet-200 dark:border-violet-900/40 text-xs text-violet-700 dark:text-violet-400">
              Secret Key aus dem <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Stripe Dashboard <ExternalLink className="w-3 h-3 inline" /></a>. Webhook Secret erhältst du beim Registrieren dieser Endpoint-URL unter <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="underline font-medium">Webhooks <ExternalLink className="w-3 h-3 inline" /></a> (Ziel-URL: <code>/api/webhook/stripe</code>). Ohne Webhook Secret werden eingehende Webhook-Events abgelehnt statt ungeprüft vertraut.
            </div>
          </ProviderCard>

          <ChannelConfig
            icon={CreditCard} iconColor="text-violet-500" title="Online-Zahlungen"
            enabled={stripeConfig.enabled} onToggle={(v) => setStripeConfig(p => ({...p, enabled: v}))}
          >
            <p className="text-xs text-muted-foreground">
              Aktiviert den "Online-Zahlung (Stripe)"-Weg bei der Mitgliedschafts-Zuweisung.
            </p>
          </ChannelConfig>
        </TabsContent>

        {/* ─── OneClickGym ─── */}
        <TabsContent value="oneclickgym" className="mt-4 space-y-4">
          <ChannelConfig
            icon={Dumbbell} iconColor="text-orange-500" title="Probetraining-Leads aus OneClickGym"
            enabled={oneClickGymConfig.enabled} onToggle={(v) => setOneClickGymConfig(p => ({...p, enabled: v}))}
          >
            <div>
              <Label className="text-xs">Webhook-URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  readOnly
                  value={`${window.location.origin}${oneClickGymConfig.webhook_path}`}
                  className="h-9 mono text-xs"
                  data-testid="oneclickgym-webhook-url"
                />
                <Button variant="outline" size="sm" className="h-9 px-2.5 flex-shrink-0" onClick={copyOneClickGymUrl} data-testid="copy-oneclickgym-url-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <Button
              variant="outline" size="sm" onClick={handleRegenerateOneClickGymSecret} disabled={regeneratingSecret}
              className="gap-1.5 h-8 text-xs" data-testid="regenerate-oneclickgym-secret-btn"
            >
              {regeneratingSecret ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Secret neu generieren
            </Button>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-900/40 text-xs text-orange-700 dark:text-orange-400 space-y-1.5">
              <p className="font-medium">So richtest du es ein (funktioniert für den Erwachsenen- UND den Kindertraining-Funnel):</p>
              <p>1. In OneClickGym bei beiden Funnels beim Schritt nach dem Wunschtermin einen Webhook konfigurieren und diese URL eintragen.</p>
              <p>2. Felder mappen: <code className="font-mono">first_name, last_name, email, phone, trial_date, trial_time</code> (oder <code className="font-mono">trial_datetime</code>), <code className="font-mono">experience, notes</code> — plus beim Erwachsenen-Funnel <code className="font-mono">interest</code>, beim Kindertraining-Funnel stattdessen <code className="font-mono">age</code>.</p>
              <p>3. <code className="font-mono">interest</code> muss einer der vier Funnel-Werte sein (Boxen/Kickboxen, Grappling/Luta Livre, MMA, Ladies Boxing Club); <code className="font-mono">age</code> eine der drei Altersgruppen (5-9, 10-13, 14-17) — beide werden automatisch anhand des Kursnamens dem passenden Kurs zugeordnet.</p>
              <p>4. Damit automatisch ein Kursplatz reserviert wird, muss der Zielkurs unter Kursplan → Kurs bearbeiten ein "Probetraining-Plätze"-Kontingent &gt; 0 haben.</p>
            </div>
          </ChannelConfig>
        </TabsContent>
      </Tabs>
    </div>
  );
}


/* ─── Provider Card ─── */
function ProviderCard({ icon: Icon, iconColor, iconBg, title, verified, children }) {
  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg || 'bg-muted'}`}>
              <Icon className={`w-4 h-4 ${iconColor}`} />
            </div>
            <CardTitle className="text-sm">{title}</CardTitle>
          </div>
          {verified ? (
            <Badge className="bg-green-500 text-white text-[10px] gap-1"><CheckCircle2 className="w-3 h-3" />Connected</Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px] gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" />Not verified</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}


/* ─── Channel Config Card ─── */
function ChannelConfig({ icon: Icon, iconColor, title, enabled, onToggle, children }) {
  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Icon className={`w-4 h-4 ${iconColor}`} />{title}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={enabled ? 'default' : 'secondary'} className="text-[10px]">{enabled ? 'Enabled' : 'Disabled'}</Badge>
            <Switch checked={enabled || false} onCheckedChange={onToggle} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}


/* ─── Template List ─── */
function TemplateList({ templates, onAdd, onUpdate, onRemove, varsHint, hasSubject }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-semibold">Nachrichtenvorlagen</Label>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={onAdd}><Plus className="w-3 h-3" />Hinzufügen</Button>
      </div>
      <div className="space-y-3">
        {templates.map((tmpl, i) => (
          <div key={`tmpl-${tmpl.name || ''}-${i}`} className="border rounded-lg p-3 space-y-2 bg-muted/10 group">
            <div className="flex items-center gap-2">
              <Input value={tmpl.name} onChange={(e) => onUpdate(i, 'name', e.target.value)} className="h-7 text-xs font-medium flex-1" />
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={() => onRemove(i)}><X className="w-3 h-3 text-muted-foreground" /></Button>
            </div>
            {hasSubject && <Input value={tmpl.subject || ''} onChange={(e) => onUpdate(i, 'subject', e.target.value)} className="h-7 text-xs" placeholder="Betreff..." />}
            <Textarea value={tmpl.body} onChange={(e) => onUpdate(i, 'body', e.target.value)} rows={hasSubject ? 4 : 2} className="text-xs" />
            <p className="text-[9px] text-muted-foreground">Variables: {varsHint}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ─── Massen-E-Mail (Kommandozentrale) ───
   Zielgruppe per Filter, DSGVO-Einwilligung als Pflichtfilter, Vorschau mit
   Empfängerzahl, expliziter Bestätigungs-Schritt vor dem Versand. */
function BulkEmailCard() {
  const { settings } = useSettings();
  const [filters, setFilters] = useState({ stage: '', source: '', bereich: '', inactive_days: '' });
  const [consentMode, setConsentMode] = useState('marketing');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);

  const stages = settings.pipeline_stages || [];
  const sources = settings.lead_sources || [];
  const bereiche = settings.bereiche || [];

  const buildFilters = () => {
    const f = {};
    if (filters.stage) f.stage = filters.stage;
    if (filters.source) f.source = filters.source;
    if (filters.bereich) f.bereich = filters.bereich;
    if (filters.inactive_days) f.inactive_days = parseInt(filters.inactive_days);
    return f;
  };

  const doPreview = async () => {
    setBusy(true); setResult(null); setConfirmed(false);
    try {
      const res = await integrationsAPI.bulkEmailPreview({ filters: buildFilters(), consent_mode: consentMode });
      setPreview(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Vorschau fehlgeschlagen'); }
    setBusy(false);
  };

  const doSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Betreff und Text erforderlich'); return; }
    if (!confirmed) { toast.error('Bitte Versand bestätigen (Checkbox)'); return; }
    setBusy(true);
    try {
      const res = await integrationsAPI.bulkEmailSend({
        filters: buildFilters(), consent_mode: consentMode,
        subject: subject.trim(), body: body.trim(), confirm: true,
      });
      setResult(res.data);
      setPreview(null); setConfirmed(false);
      toast.success(`${res.data.sent} E-Mails gesendet${res.data.failed ? `, ${res.data.failed} fehlgeschlagen` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
    setBusy(false);
  };

  return (
    <Card className="border-border/60" data-testid="bulk-email-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2"><Send className="w-4 h-4 text-indigo-500" /> Massen-E-Mail</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Pipeline-Stufe</Label>
            <select value={filters.stage} onChange={(e) => setFilters(f => ({ ...f, stage: e.target.value }))} data-testid="bulk-stage"
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm">
              <option value="">Alle</option>
              {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Quelle</Label>
            <select value={filters.source} onChange={(e) => setFilters(f => ({ ...f, source: e.target.value }))} data-testid="bulk-source"
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm">
              <option value="">Alle</option>
              {sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Bereich</Label>
            <select value={filters.bereich} onChange={(e) => setFilters(f => ({ ...f, bereich: e.target.value }))} data-testid="bulk-bereich"
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm">
              <option value="">Alle</option>
              {bereiche.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Inaktiv seit (Tage)</Label>
            <Input type="number" min="0" value={filters.inactive_days} placeholder="z. B. 30"
              onChange={(e) => setFilters(f => ({ ...f, inactive_days: e.target.value }))} className="h-9 text-xs" data-testid="bulk-inactive" />
          </div>
        </div>

        <div>
          <Label className="text-xs">Einwilligung (DSGVO)</Label>
          <select value={consentMode} onChange={(e) => setConsentMode(e.target.value)} data-testid="bulk-consent"
            className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm">
            <option value="marketing">Marketing — NUR Empfänger mit ausdrücklicher Werbe-Einwilligung</option>
            <option value="betrieb">Betriebsinfo — alle außer Widerspruch (kein Marketing! z. B. Schließtage)</option>
          </select>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Betreff</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Betreff — {lead_name}, {company} werden ersetzt" className="h-9 text-xs" data-testid="bulk-subject" />
          </div>
          <div>
            <Label className="text-xs">Text</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder={'Hallo {lead_name},\n\n…'} className="text-xs" data-testid="bulk-body" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={doPreview} disabled={busy} className="gap-1.5 h-8 text-xs" data-testid="bulk-preview-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" />} Empfänger-Vorschau
          </Button>
          {preview && (
            <Badge variant="secondary" className="text-xs" data-testid="bulk-preview-count">
              {preview.count} Empfänger{preview.skipped_no_email > 0 ? ` · ${preview.skipped_no_email} ohne E-Mail übersprungen` : ''}
            </Badge>
          )}
        </div>

        {preview && preview.count > 0 && (
          <div className="bg-muted/30 rounded-lg p-3 space-y-2" data-testid="bulk-confirm-box">
            <p className="text-[11px] text-muted-foreground">
              Beispiel-Empfänger: {preview.sample.map(s => s.name).slice(0, 5).join(', ')}{preview.count > 5 ? ' …' : ''}
            </p>
            {preview.count > preview.max_per_send && (
              <p className="text-[11px] text-destructive font-medium">Mehr als {preview.max_per_send} Empfänger — bitte Filter enger fassen.</p>
            )}
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 accent-marke" data-testid="bulk-confirm-cb" />
              <span className="text-xs">Ich bestätige den Versand an <b>{preview.count} Empfänger</b> ({consentMode === 'marketing' ? 'nur mit Werbe-Einwilligung' : 'Betriebsinfo'}).</span>
            </label>
            <Button size="sm" onClick={doSend} disabled={busy || !confirmed || preview.count > preview.max_per_send}
              className="gap-1.5 h-8 text-xs" data-testid="bulk-send-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Jetzt an {preview.count} Empfänger senden
            </Button>
          </div>
        )}

        {result && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/40 rounded-lg p-3 text-xs text-emerald-700 dark:text-emerald-400" data-testid="bulk-result">
            ✓ {result.sent} gesendet{result.failed > 0 ? ` · ${result.failed} fehlgeschlagen` : ''} — Protokoll unter Nachrichten je Lead.
          </div>
        )}

        <p className="text-[10px] text-muted-foreground">
          Maximal 200 Empfänger pro Versand · Platzhalter: {'{lead_name}'}, {'{agent_name}'}, {'{company}'} · Jeder Versand wird im Audit-Log protokolliert.
        </p>
      </CardContent>
    </Card>
  );
}
